<template>
  <LModal
    :visible="visible"
    size="xl"
    :title="`${t('components.event-action.event.EventLinkageModal.title')}: ${props.event.id} | ${'description' in props.event ? props.event.description : ''}`"
    ok-variant="success"
    :loading="loading"
    :submit="
      async () => {
        await addLinkage();
        return { success: true };
      }
    "
    @update:visible="$emit('update:visible', $event)"
  >
    <div style="min-height: 50vh">
      <InputRow
        v-if="originBoardId == null"
        :display-name="t('components.event-action.event.EventLinkageModal.noBoardConnection')"
        required
        name="events"
      >
        &nbsp;
      </InputRow>
      <InputRow
        v-else
        v-model="selected"
        name="events"
        :display-name="t('components.event-action.event.EventLinkageModal.events')"
        :hide-label="true"
      >
        <template #default="{ validation: { valid: state } }">
          <CMSearchField
            v-model:search-text="searchText"
            v-model:selected-result="selected"
            :inline="true"
            :results="results"
            :multiple="true"
            :state="state"
          />
        </template>
      </InputRow>
    </div>
  </LModal>
</template>
<script setup lang="ts">
import { ref, type PropType, computed, watch } from "vue";
import { useTranslation } from "@/language/translation";
import {
  type EventListEventFieldsFragment,
  EventType,
  useLinkEventMutation,
  useGSSearchGloballyQuery,
  GSIncludeType,
  GSSearchResultType,
  type GSSearchGloballyQuery,
} from "@/graphql";
import { eventIdPrefix } from "./EventActionIdPrefix";
import LModal from "@/components/LModal.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import CMSearchField from "@/modules/commandMenu/components/CMSearchField.vue";
import { cloneDeep } from "lodash";
import notEmpty from "@/functions/notEmpty";
import type { SearchOptionType } from "@/modules/commandMenu/helper/CommandMenuType";

const selected = ref<SearchOptionType[]>([]);

const props = defineProps({
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
  visible: {
    type: Boolean,
    required: true,
  },
});
defineEmits({ "update:visible": (val: boolean) => val });
const { t } = useTranslation();

const { mutate, loading: loadingLinkEvent } = useLinkEventMutation({});

const searchText = ref("");

const forbiddenEvents = computed(() => [
  {
    eventId: props.event.id,
    eventType: props.event.__typename,
  },
]);

const originBoardId = computed(
  () => props.event.boardConnection.find((con) => con.status === 0)?.boardId,
);

const {
  result: backendSearchQueryResult,
  loading: backendSearchQueryLoading,
  refetch,
} = useGSSearchGloballyQuery(
  computed(() => ({
    searchString: searchText.value,
    options: {
      boardId: originBoardId.value,
      include: Object.keys(EventType).filter(
        (eventType) => eventType !== EventType.AuditResult,
      ) as GSIncludeType[],
    },
  })),
  computed(() => ({
    enabled: props.visible === true && originBoardId.value != null,
  })),
);

watch(
  () => props.visible,
  async () => {
    searchText.value = "";

    const _query = await refetch({
      searchString: "",
      options: {
        boardId: originBoardId.value,
        include: Object.keys(EventType).filter(
          (eventType) => eventType !== EventType.AuditResult,
        ) as GSIncludeType[],
      },
    });
    const events = cloneDeep(_query?.data?.GSSearchGlobally ?? []).map((el, index) => ({
      ...el,
      index,
    }));
    selected.value = props.event.linkedEvents.map((el) => {
      const foundEvent = events.find((ev) =>
        "eventId" in ev.meta ? ev.meta.eventId === el.id : undefined,
      )!;
      return {
        name: getSearchResultName(foundEvent!),
        meta: foundEvent.meta,
        searchResultType: GSSearchResultType.EVENT,
        index: foundEvent?.index,
        overlay: true,
      };
    });
  },
  {
    deep: true,
    immediate: true,
  },
);

const backendSearchResult = computed(() => backendSearchQueryResult.value?.GSSearchGlobally);

function getSearchResultName(searchResult: GSSearchGloballyQuery["GSSearchGlobally"][0]) {
  const eventMeta = searchResult.meta;
  if (eventMeta.__typename !== "GSSearchResultEventMeta") {
    return t("global.na");
  }
  return t("components.event-action.event.EventLinkageModal.event", {
    eventType: t(`global.EABTypes.${eventMeta.eventType}`),
    eventId: `${eventIdPrefix[eventMeta.eventType]}${eventMeta.eventId}`,
    description:
      eventMeta.deletedAt != null
        ? t("components.event-action.event.EventLinkageModal.archived")
        : t("components.event-action.event.EventLinkageModal.open"),
  });
}

const loading = computed(() => backendSearchQueryLoading.value || loadingLinkEvent.value);

const results = computed(() => {
  const resultEntries: SearchOptionType[] = [
    ...(backendSearchResult.value?.map((searchResult, index) => ({
      name: getSearchResultName(searchResult),
      searchResultType: searchResult.type,
      type: searchResult.type,
      meta: searchResult.meta,
      index,
      overlay:
        searchResult.type === GSSearchResultType.EVENT ||
        searchResult.type === GSSearchResultType.ACTION,
    })) ?? []),
  ];

  if (loading.value) {
    resultEntries.push({
      name: t("global.loading"),
      searchResultType: GSSearchResultType.LOADING,
      meta: null,
      index: resultEntries.length,
    });
  }

  /**
   * Remove itself and already linked events from the result list to prevent event linkage brick
   */
  return resultEntries.filter((searchResult) => {
    const meta = searchResult.meta;
    if (meta?.__typename !== "GSSearchResultEventMeta") {
      return t("global.na");
    }
    return !forbiddenEvents.value.some(
      (forbiddenEvent) =>
        forbiddenEvent.eventId === meta?.eventId && forbiddenEvent.eventType === meta?.eventType,
    );
  });
});

async function addLinkage() {
  await mutate({
    boardId: originBoardId.value,
    sourceEvent: {
      eventId: props.event.id,
      eventType: props.event.__typename as EventType,
    },
    targetEvents: selected.value
      .map((el) => {
        if (el.meta?.__typename !== "GSSearchResultEventMeta") {
          return undefined;
        }
        return {
          eventId: el.meta.eventId,
          eventType: el.meta.eventType,
        };
      })
      .filter(notEmpty),
  });
}
</script>
<style></style>
